import { router } from '@/Router';
import type { CategoriesType } from '@/Utility/hooks/dataLoaders/useCategorySetter';
import type { GCP_VertexSearch_Single_Query$data } from '@/graphql/queries/GCP.query';
import type { ZnglyContext_TopBarNavigation_Query$data } from '@/graphql/queries/ZnglyContext.query';
import type {
  RootQueryToZnglistConnectionEdge,
  RootQueryZnglyVideosArgs,
  User,
  Hub as ZnglyHub,
  ZnglyInfo,
  ZnglyPrivacyUserConsentLevelEnum,
  ZnglyVideo,
  ZnglyVideoConnection,
} from '@/graphql/types';
import type { DeepPartial } from 'ts-essentials';
import { create } from 'zustand';

type HubsAppQuery = DeepPartial<ZnglyContext_TopBarNavigation_Query$data['hubs']>;
type ZnglistsAppQuery = DeepPartial<ZnglyContext_TopBarNavigation_Query$data['znglists']>;

export type AppProps = {
  // inertia shared props
  user?: User | null;
  pageType:
    | 'single'
    | 'search'
    | 'category'
    | 'front_page'
    | '404'
    | 'znglist'
    | 'hub'
    | 'view-all'
    | 'privacy-policy'
    | 'unknown';
  znglyInfo: ZnglyInfo['znglyInfo'] | null;
  categories: CategoriesType | null;
  znglists: ZnglistsAppQuery;
  hubs: HubsAppQuery;

  info: {
    canDoAiSearch: boolean;
    homeUrl: string;
    dashboardUrl: string;
    hasAnalytics: boolean;
  };
  privacyConsentLevel?: ZnglyPrivacyUserConsentLevelEnum;

  // potential server side props
  barTitle?: string;
  posts?:
    | (Omit<ZnglyVideoConnection, 'nodes'> & {
        queryArgs?: RootQueryZnglyVideosArgs;
        activeNodeIdx?: number;
        isAi?: boolean;
        summary?: string;
      })
    | null;
  znglist?: RootQueryToZnglistConnectionEdge['node'] | null;
  hub?: ZnglyHub | null;
};

export type AppStore = {
  /**
   * Whether the app is in preview mode
   */
  isAppPreview: boolean;
  /**
   * Set the app preview mode
   */
  setIsAppPreview: (isAppPreview: boolean) => void;

  appPreview?: {
    hasEditingChanges?: boolean;
    znglist?: {
      viewAs?: 'currentUser' | 'postAuthor';
    };
  } | null;
  setAppPreview: (appPreview?: AppStore['appPreview'] | null) => void;

  /**
   * Whether the app is in dashboard mode
   */
  isDashboard: boolean;

  // app functionality
  showHeader: boolean;
  setShowHeader: (showHeader: boolean) => void;
  showFooter: boolean;
  setShowFooter: (showFooter: boolean) => void;
  showTopBar: boolean;
  setShowTopBar: (showTopBar: boolean) => void;
  isPageLoading: boolean;
  setIsPageLoading: (isPageLoading: boolean) => void;
  barView: 'list' | 'grid';
  setBarView: (barView: 'list' | 'grid') => void;

  // to do with catalog content
  playerNode: ZnglyVideo | null;
  setPlayerNode: (playerNode: ZnglyVideo | null) => void;
  nodes: ZnglyVideo[];
  setNodes: (nodes: ZnglyVideo[]) => void;
  catalogLoadMore?: () => void;
  isCatalogLoadingMore: boolean;
  isLoadingVertexSearch: boolean;
  setIsLoadingVertexSearch: (isLoadingVertexSearch: boolean) => void;
  vertexSearchAgent?: GCP_VertexSearch_Single_Query$data['vertexSearchAgent'] | null;
  setVertexSearchAgent: (
    vertexSearchAgent: GCP_VertexSearch_Single_Query$data['vertexSearchAgent'] | null | undefined
  ) => void;

  catalogNodes: ZnglyVideo[];

  // inertia shared props
  barTitle: AppProps['barTitle'];
  setBarTitle: (barTitle: string) => void;
  znglyInfo: ZnglyInfo['znglyInfo'];
  categories: AppProps['categories'];
  znglists: AppProps['znglists'];
  info: AppProps['info'];
  pageType: AppProps['pageType'] | null;
  setPageType: (pageType: AppProps['pageType']) => void;
  hubs: AppProps['hubs'];

  resetInertia: () => void;

  // potential server side props
  znglist?: AppProps['znglist'];
  setZnglist: (znglist: AppProps['znglist']) => void;
  hub?: AppProps['hub'];
  setHub: (hub: AppProps['hub']) => void;

  // dev tools
  showTsrDevTools: boolean;
};

export const useAppStore = create<AppStore>((set, get) => ({
  isAppPreview: false,
  setIsAppPreview: (isAppPreview: boolean) => set({ isAppPreview }),
  appPreview: null,
  setAppPreview: (appPreview: AppStore['appPreview'] | null) => {
    if (!appPreview) {
      set({ appPreview: null });
      return;
    }
    // deep merge the previous appPreview with the new one
    set((prev) => ({
      appPreview: {
        ...prev.appPreview,
        ...appPreview,
        znglist: {
          ...prev.appPreview?.znglist,
          ...appPreview?.znglist,
        },
      },
    }));
  },
  isDashboard: (() => {
    if (window.location.pathname.startsWith('/dash')) return true;
    return false;
  })(),
  showHeader: true,
  setShowHeader: (showHeader: boolean) => set({ showHeader }),
  showFooter: true,
  setShowFooter: (showFooter: boolean) => set({ showFooter }),
  showTopBar: true,
  setShowTopBar: (showTopBar: boolean) => set({ showTopBar }),
  isPageLoading: false,
  setIsPageLoading: (isPageLoading: boolean) => set({ isPageLoading }),
  barView: 'list',
  setBarView: (barView: AppStore['barView']) => set({ barView }),

  // to do with catalog content
  playerNode: null,
  setPlayerNode: (playerNode?: ZnglyVideo | null) => {
    if (!playerNode) {
      set({ playerNode: null });
      return;
    }

    set({ playerNode: { ...playerNode } });
  },
  nodes: [],
  setNodes: (nodes?: ZnglyVideo[]) => {
    if (!nodes?.length) {
      set({ nodes: [], catalogNodes: [] });
      return;
    }

    set((prev) => {
      const vertexSearchAgent = prev.vertexSearchAgent;

      if (!vertexSearchAgent) {
        return {
          nodes: [...nodes],
          catalogNodes: [...nodes],
        };
      }

      const { nodesFromSearch, combinedNodes } = combineSearchNodes(nodes, vertexSearchAgent);

      return {
        nodes: [...nodes],
        catalogNodes: prev.pageType === 'front_page' ? [...nodesFromSearch] : [...combinedNodes],
      };
    });
  },
  isCatalogLoadingMore: false,
  isLoadingVertexSearch: false,
  setIsLoadingVertexSearch: (isLoadingVertexSearch: boolean) => set({ isLoadingVertexSearch }),
  vertexSearchAgent: null,
  setVertexSearchAgent: (vertexSearchAgent: AppStore['vertexSearchAgent']) => {
    if (!vertexSearchAgent) {
      set((prev) => {
        const playerNode = !!prev.nodes?.[0] ? { ...prev.nodes?.[0] } : null;
        if (!!playerNode) {
          router.navigate({
            // @ts-ignore
            search: (prev) => ({ ...prev, c: playerNode?.id }),
          });
        }

        return {
          vertexSearchAgent: null,
          catalogNodes: prev.nodes,
          playerNode: playerNode,
        };
      });
      return;
    }

    set((prev) => {
      const { nodesFromSearch, combinedNodes } = combineSearchNodes(prev.nodes, vertexSearchAgent);
      const playerNode = !!nodesFromSearch?.[0] ? { ...nodesFromSearch?.[0] } : null;
      if (!!playerNode) {
        router.navigate({
          // @ts-ignore
          search: (prev) => ({ ...prev, c: playerNode?.id }),
        });
      }

      return {
        vertexSearchAgent,
        catalogNodes: (['front_page', 'search', 'category'] as Array<typeof prev.pageType>).includes(prev.pageType)
          ? [...nodesFromSearch]
          : [...combinedNodes],
        playerNode: playerNode,
      };
    });
  },
  catalogNodes: [],

  // inertia shared props
  barTitle: 'Suggested Content',
  setBarTitle: (barTitle: string) => set({ barTitle }),
  znglyInfo: null,
  categories: null,
  znglists: null,
  hubs: null,
  info: {
    canDoAiSearch: false,
    homeUrl: window.location.origin,
    dashboardUrl: `${window.location.origin}/wp-admin`,
    hasAnalytics: false,
  },
  pageType: null,
  setPageType: (pageType: AppProps['pageType']) => set({ pageType }),

  // potential server side props
  znglist: null,
  setZnglist: (z: AppProps['znglist']) => set({ znglist: !!z ? { ...z } : null }),
  hub: null,
  setHub: (h: AppProps['hub']) => set({ hub: !!h ? { ...h } : null }),

  // dev tools
  showTsrDevTools: false, // useAppStore.setState({ showTsrDevTools: true }) to enable dev tools

  resetInertia: () => {
    set({
      pageType: null,
      barTitle: '',
      znglist: null,
      hub: null,
      barView: 'list',
      nodes: [],
      catalogNodes: [],
      vertexSearchAgent: null,
    });
  },
}));

if (process.env.NODE_ENV !== 'production') {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  (window as any).useAppStore = useAppStore;
}

const combineSearchNodes = (
  nodes: ZnglyVideo[],
  vertexSearchAgent: GCP_VertexSearch_Single_Query$data['vertexSearchAgent'] | null | undefined
) => {
  if (!vertexSearchAgent)
    return {
      nodesFromSearch: [],
      combinedNodes: nodes,
    };

  const searchNodes = (vertexSearchAgent?.edges?.map((edge) => edge?.node) ?? []) as ZnglyVideo[];

  if (!searchNodes.length)
    return {
      nodesFromSearch: [],
      combinedNodes: nodes,
    };

  const newNodes = [...nodes];

  searchNodes.forEach((node) => {
    const idx = newNodes.findIndex((n) => n?.id === node?.id);
    if (idx > -1) {
      newNodes.splice(idx, 1);
    }
  });

  newNodes.unshift(...searchNodes);

  return {
    nodesFromSearch: searchNodes,
    combinedNodes: newNodes,
  };
};
